import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useAuthContext } from "@asgardeo/auth-react";
import MainLayout from "../../layouts/MainLayout";
import {
  getMyMembershipApplication,
  submitMembershipApplication,
} from "../../services/membership-application-service";
import {
  getMyProfile,
  getMobileFromProfile,
} from "../../services/scim-service";
import { CHAT_DB_SERVER_URL } from "../../config/backend-endpoints";

const MEMBERSHIP_DEFAULTS = {
  monthlyFee: "Rs. 20,000",
  yearlyFee: "Rs. 220,000",
  yearlyNote: "equivalent to one month free",
  cap: 200,
  trialDays: 30,
  replyWithinDays: 3,
};

const PLAN_CODES = {
  monthly: "CHAT_PRO_MONTHLY",
  yearly: "CHAT_PRO_YEARLY",
};

const PROFESSIONAL_STATUSES = [
  "Attorney-at-Law — private practice",
  "Attorney-at-Law — law firm",
  "State Counsel / Attorney-General's Department",
  "Judicial Officer",
  "In-house / corporate legal",
  "Legal academic",
  "Apprentice / law student",
  "Other",
];

const PRACTICE_AREAS = [
  "Civil litigation",
  "Criminal law",
  "Commercial & company",
  "Land & property",
  "Family law",
  "Labour & industrial",
  "Constitutional & administrative",
  "Intellectual property",
  "Tax",
  "Arbitration & ADR",
  "Conveyancing & notarial",
  "Other",
];

const LANGUAGES = ["English", "Sinhala", "Tamil"];

const PRIMARY_NEEDS = [
  "Case law research",
  "Document drafting",
  "Trilingual translation",
  "Summarising judgments",
  "General practice support — all of the above",
];

const INITIAL_FORM = {
  fullName: "",
  email: "",
  phone: "",
  professionalStatus: "",
  enrolmentYear: "",
  firm: "",
  practiceAreas: [],
  languages: [],
  primaryNeed: "",
  recurringTask: "",
  hardestProblem: "",
  termsAcknowledged: false,
  // Honeypot — hidden from humans; bots that fill it are dropped silently.
  website: "",
};

const labelCls = "block text-14 font-medium text-pl-text-secondary mb-2";
// 16px input text is deliberate: below 16px iOS Safari zooms the page on focus.
const inputCls =
  "w-full bg-pl-bg border border-pl-border rounded-pl px-4 py-3 text-16 text-pl-text-primary placeholder:text-pl-text-tertiary focus:outline-none focus:border-pl-accent transition-colors duration-200";
const errorCls = "mt-2 text-13 text-pl-error";
const lockedCls =
  "bg-pl-bg-subtle text-pl-text-secondary cursor-default focus:border-pl-border";

const LockedHint = () => (
  <p className="mt-2 text-13 text-pl-text-tertiary">
    From your paralegal.lk account.
  </p>
);

const formatPlanAmount = (amount, currency) => {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return null;
  }

  const formatted = new Intl.NumberFormat("en-LK", {
    maximumFractionDigits: Number.isInteger(numeric) ? 0 : 2,
  }).format(numeric);
  const currencyCode = String(currency || "").trim().toUpperCase();
  return currencyCode === "LKR"
    ? `Rs. ${formatted}`
    : `${currencyCode} ${formatted}`.trim();
};

const findPlanByCode = (plans, planCode) =>
  (Array.isArray(plans) ? plans : []).find(
    (plan) => String(plan?.plan_code || "").trim() === planCode
  );

const buildMembershipPricing = (plans) => {
  const monthly = findPlanByCode(plans, PLAN_CODES.monthly);
  const yearly = findPlanByCode(plans, PLAN_CODES.yearly);
  const monthlyFee =
    formatPlanAmount(monthly?.amount, monthly?.currency) ||
    MEMBERSHIP_DEFAULTS.monthlyFee;
  const yearlyFee =
    formatPlanAmount(yearly?.amount, yearly?.currency) ||
    MEMBERSHIP_DEFAULTS.yearlyFee;
  const monthlyAmount = Number(monthly?.amount);
  const yearlyAmount = Number(yearly?.amount);
  let yearlyNote = MEMBERSHIP_DEFAULTS.yearlyNote;

  if (Number.isFinite(monthlyAmount) && Number.isFinite(yearlyAmount)) {
    const savings = monthlyAmount * 12 - yearlyAmount;
    if (savings > 0) {
      const savingsLabel = formatPlanAmount(savings, yearly?.currency);
      yearlyNote =
        savings >= monthlyAmount * 0.9 && savings <= monthlyAmount * 1.1
          ? "equivalent to one month free"
          : `save ${savingsLabel} compared to monthly billing`;
    } else {
      yearlyNote = "billed annually";
    }
  }

  return {
    ...MEMBERSHIP_DEFAULTS,
    monthlyFee,
    yearlyFee,
    yearlyNote,
  };
};

const SectionLabel = ({ n, label }) => (
  <div className="relative border-t border-pl-border mt-12 mb-10 md:mb-12">
    <div className="absolute -top-[10px] left-0 bg-pl-bg pr-4 font-mono text-11 md:text-12 uppercase tracking-[0.22em] flex items-baseline gap-3">
      <span className="text-pl-accent">§ {n}</span>
      <span className="text-pl-text-primary">{label}</span>
    </div>
  </div>
);

const ToggleChip = ({ selected, onToggle, children }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-pressed={selected}
    className={`px-4 py-2.5 rounded-pl border text-14 transition-colors duration-200 ${
      selected
        ? "bg-pl-text-primary text-pl-bg border-pl-text-primary"
        : "bg-transparent text-pl-text-secondary border-pl-border hover:border-pl-border-strong hover:text-pl-text-primary"
    }`}
  >
    {children}
  </button>
);

const Field = ({ label, htmlFor, required, error, children }) => (
  <div>
    <label className={labelCls} htmlFor={htmlFor}>
      {label}
      {required && <span className="text-pl-accent ml-1">*</span>}
    </label>
    {children}
    {error && <p className={errorCls}>{error}</p>}
  </div>
);

const ApplyForMembership = () => {
  const { state, getBasicUserInfo, getDecodedIDToken, getAccessToken } =
    useAuthContext();
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | checking | submitting | submitted | error | auth-required
  const [existingApplication, setExistingApplication] = useState(null);
  const [membership, setMembership] = useState(MEMBERSHIP_DEFAULTS);
  // Identity fields come from the Asgardeo account and cannot be amended
  // here; a field stays editable only if the account has no value for it.
  const [locked, setLocked] = useState({
    fullName: false,
    email: false,
    phone: false,
  });

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const response = await fetch(`${CHAT_DB_SERVER_URL}/chat-plans`, {
          method: "GET",
          headers: { Accept: "application/json" },
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.detail || "Unable to load membership pricing.");
        }
        if (!cancelled) {
          setMembership(buildMembershipPricing(payload?.plans));
        }
      } catch (error) {
        console.warn("Membership pricing load failed:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!state.isAuthenticated) return;
    let cancelled = false;

    (async () => {
      const [info, decodedToken, scimProfile] = await Promise.all([
        getBasicUserInfo().catch(() => ({})),
        getDecodedIDToken().catch(() => ({})),
        getAccessToken()
          .then((token) => (token ? getMyProfile(token) : null))
          .catch(() => null),
      ]);
      if (cancelled) return;

      const fullName =
        [info?.givenName, info?.familyName].filter(Boolean).join(" ") ||
        info?.displayName ||
        "";
      const email = info?.email || decodedToken?.email || "";
      const phone =
        (scimProfile ? getMobileFromProfile(scimProfile) : "") ||
        decodedToken?.phone_number ||
        "";

      setForm((prev) => ({
        ...prev,
        fullName: prev.fullName || fullName,
        email: prev.email || email,
        phone: prev.phone || phone,
      }));
      setLocked({
        fullName: Boolean(fullName),
        email: Boolean(email),
        phone: Boolean(phone),
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [state.isAuthenticated, getBasicUserInfo, getDecodedIDToken, getAccessToken]);


  useEffect(() => {
    if (!state.isAuthenticated) return;
    let cancelled = false;

    (async () => {
      try {
        setStatus((prev) => (prev === "idle" ? "checking" : prev));
        const accessToken = await getAccessToken();
        const result = await getMyMembershipApplication(accessToken);
        if (cancelled) return;

        if (result?.exists) {
          setExistingApplication(result);
          setForm((prev) => ({
            ...prev,
            email: prev.email || result.email || "",
          }));
          setStatus("submitted");
        } else {
          setStatus((prev) => (prev === "checking" ? "idle" : prev));
        }
      } catch (error) {
        if (!cancelled) {
          console.warn("Membership application status check failed:", error);
          setStatus((prev) => (prev === "checking" ? "idle" : prev));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [state.isAuthenticated, getAccessToken]);

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const toggleListField = (name, value) => {
    setField(
      name,
      form[name].includes(value)
        ? form[name].filter((item) => item !== value)
        : [...form[name], value]
    );
  };

  const validate = () => {
    const next = {};
    if (!form.fullName.trim()) next.fullName = "Please enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      next.email = "Please enter a valid email address.";
    if (!form.phone.trim()) next.phone = "Please enter a contact number.";
    if (!form.professionalStatus)
      next.professionalStatus = "Please select your professional status.";
    if (!form.primaryNeed)
      next.primaryNeed = "Please select what you need most.";
    if (!form.termsAcknowledged)
      next.termsAcknowledged =
        "Please confirm you have read the terms of membership.";
    return next;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      const firstInvalid = event.target.querySelector("[aria-invalid='true']");
      if (firstInvalid) firstInvalid.focus();
      return;
    }

    if (form.website) {
      // Honeypot tripped — act like a success, store nothing.
      setStatus("submitted");
      return;
    }

    if (!state.isAuthenticated) {
      setStatus("auth-required");
      return;
    }

    setStatus("submitting");
    try {
      const accessToken = await getAccessToken();
      const result = await submitMembershipApplication({
        full_name: form.fullName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        professional_status: form.professionalStatus,
        enrolment_year: form.enrolmentYear.trim() || null,
        firm: form.firm.trim() || null,
        practice_areas: form.practiceAreas,
        languages: form.languages,
        primary_need: form.primaryNeed,
        recurring_task: form.recurringTask.trim() || null,
        hardest_problem: form.hardestProblem.trim() || null,
        terms_acknowledged: form.termsAcknowledged,
        source: "paralegal.lk/apply-for-membership",
      }, accessToken);
      setExistingApplication({
        exists: true,
        application_id: result?.application_id,
        status: result?.status || "new",
        email: form.email.trim(),
      });
      setStatus("submitted");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Membership application failed:", error);
      setStatus("error");
    }
  };

  return (
    <MainLayout>
      <Helmet>
        <title>Become a Professional Member at paralegal.lk</title>
        <meta
          name="description"
          content="Apply for Professional Membership at paralegal.lk — a privacy-first legal AI workspace with specialised agents for Sri Lankan legal research, drafting, review, and translation. Limited seats."
        />
        <link
          rel="canonical"
          href="https://www.paralegal.lk/apply-for-membership"
        />
      </Helmet>

      <article className="apply-page w-full max-w-[1080px] mx-auto px-6 md:px-12 py-10 md:py-16 text-pl-text-primary">
        {/* Masthead */}
        <header className="flex flex-wrap justify-between items-baseline gap-y-2 pb-4 mb-10 md:mb-14 border-b border-pl-border font-mono text-10 md:text-11 uppercase tracking-[0.22em] text-pl-text-tertiary">
          <div className="flex items-baseline gap-3">
            <span>paralegal.lk / professional membership</span>
          </div>
          <span>Limited seats</span>
        </header>

        {status === "submitted" ? (
          <SubmittedNotice
            email={form.email}
            existingApplication={existingApplication}
          />
        ) : (
          <>
            {/* Hero */}
            <div className="entry e-0 mb-14 md:mb-16">
              <h1 className="font-display leading-[1.05] tracking-[-0.015em] text-[34px] sm:text-[44px] md:text-[58px] lg:text-[64px]">
                Become a{" "}
                <em className="italic text-pl-accent md:whitespace-nowrap">
                  Professional Member
                </em>{" "}
                at paralegal.lk
              </h1>
            </div>

            <div className="entry e-1 mb-16 md:mb-20 grid grid-cols-1 lg:grid-cols-[minmax(0,66ch)_1fr] gap-x-20 gap-y-10 items-start">
              <div className="space-y-8">
                {[
                  {
                    n: "01",
                    title: "Legal AI workspace built around privacy",
                    body: "Advanced AI agents for research, drafting, review, and translation — built for Sri Lankan law.",
                  },
                  {
                    n: "02",
                    title: "Practical legal AI training",
                    body: "We have tested these tools in real legal work for over 12 months, and we share the lessons we have learned along the way.",
                  },
                  {
                    n: "03",
                    title: "Hands-on support and custom solutions",
                    body: "Direct support for complex legal-tech problems, from workflow design to bespoke automation.",
                  },
                ].map(({ n, title, body }) => (
                  <div key={n} className="flex gap-4">
                    <span className="font-mono text-12 md:text-13 text-pl-accent pt-[3px]">
                      {n}
                    </span>
                    <div>
                      <h2 className="text-16 md:text-18 font-semibold text-pl-text-primary mb-1">
                        {title}
                      </h2>
                      <p className="text-15 md:text-16 leading-[1.7] text-pl-text-secondary">
                        {body}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-x-8 gap-y-5">
                {[
                  {
                    term: "On acceptance",
                    detail: `${membership.trialDays} days of free access to the legal AI workspace`,
                  },
                  {
                    term: "Seats",
                    detail: "Limited — membership is by application",
                  },
                ].map(({ term, detail }) => (
                  <div key={term} className="border-t border-pl-border pt-3">
                    <dt className="font-mono text-10 md:text-11 uppercase tracking-[0.18em] text-pl-text-tertiary mb-1">
                      {term}
                    </dt>
                    <dd className="text-14 leading-[1.6] text-pl-text-primary">
                      {detail}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              {/* § I — The Applicant */}
              <SectionLabel n="I" label="The Applicant" />

              <div className="entry e-2 mb-16 md:mb-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-8">
                <Field
                  label="Full name"
                  htmlFor="apply-full-name"
                  required
                  error={errors.fullName}
                >
                  <input
                    id="apply-full-name"
                    type="text"
                    autoComplete="name"
                    readOnly={locked.fullName}
                    className={`${inputCls} ${locked.fullName ? lockedCls : ""}`}
                    value={form.fullName}
                    onChange={(e) => setField("fullName", e.target.value)}
                    aria-invalid={Boolean(errors.fullName)}
                  />
                  {locked.fullName && <LockedHint />}
                </Field>

                <Field
                  label="Email"
                  htmlFor="apply-email"
                  required
                  error={errors.email}
                >
                  <input
                    id="apply-email"
                    type="email"
                    autoComplete="email"
                    readOnly={locked.email}
                    className={`${inputCls} ${locked.email ? lockedCls : ""}`}
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                    aria-invalid={Boolean(errors.email)}
                  />
                  {locked.email && <LockedHint />}
                </Field>

                <Field
                  label="Phone"
                  htmlFor="apply-phone"
                  required
                  error={errors.phone}
                >
                  <input
                    id="apply-phone"
                    type="tel"
                    autoComplete="tel"
                    placeholder="07X XXX XXXX"
                    readOnly={locked.phone}
                    className={`${inputCls} ${locked.phone ? lockedCls : ""}`}
                    value={form.phone}
                    onChange={(e) => setField("phone", e.target.value)}
                    aria-invalid={Boolean(errors.phone)}
                  />
                  {locked.phone && <LockedHint />}
                </Field>

                <Field
                  label="Professional status"
                  htmlFor="apply-status"
                  required
                  error={errors.professionalStatus}
                >
                  <select
                    id="apply-status"
                    className={`${inputCls} appearance-none bg-pl-bg`}
                    value={form.professionalStatus}
                    onChange={(e) =>
                      setField("professionalStatus", e.target.value)
                    }
                    aria-invalid={Boolean(errors.professionalStatus)}
                  >
                    <option value="">Select…</option>
                    {PROFESSIONAL_STATUSES.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field
                  label="Year of enrolment (if applicable)"
                  htmlFor="apply-enrolment"
                >
                  <input
                    id="apply-enrolment"
                    type="number"
                    min="1950"
                    max={currentYear}
                    placeholder="e.g. 2012"
                    className={inputCls}
                    value={form.enrolmentYear}
                    onChange={(e) => setField("enrolmentYear", e.target.value)}
                  />
                </Field>

                <Field label="Firm / chambers / institution" htmlFor="apply-firm">
                  <input
                    id="apply-firm"
                    type="text"
                    autoComplete="organization"
                    className={inputCls}
                    value={form.firm}
                    onChange={(e) => setField("firm", e.target.value)}
                  />
                </Field>

                {/* Honeypot — visually hidden, tab-skipped */}
                <div className="hidden" aria-hidden="true">
                  <label htmlFor="apply-website">Website</label>
                  <input
                    id="apply-website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={(e) => setField("website", e.target.value)}
                  />
                </div>
              </div>

              {/* § II — Your Practice */}
              <SectionLabel n="II" label="Your Practice" />

              <div className="entry e-3 mb-16 md:mb-20 space-y-8">
                <div>
                  <span className={labelCls}>Areas of practice</span>
                  <div className="flex flex-wrap gap-2">
                    {PRACTICE_AREAS.map((area) => (
                      <ToggleChip
                        key={area}
                        selected={form.practiceAreas.includes(area)}
                        onToggle={() => toggleListField("practiceAreas", area)}
                      >
                        {area}
                      </ToggleChip>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <span className={labelCls}>Working languages</span>
                    <div className="flex flex-wrap gap-2">
                      {LANGUAGES.map((language) => (
                        <ToggleChip
                          key={language}
                          selected={form.languages.includes(language)}
                          onToggle={() =>
                            toggleListField("languages", language)
                          }
                        >
                          {language}
                        </ToggleChip>
                      ))}
                    </div>
                  </div>

                  <Field
                    label="What do you need most from paralegal.lk?"
                    htmlFor="apply-primary-need"
                    required
                    error={errors.primaryNeed}
                  >
                    <select
                      id="apply-primary-need"
                      className={`${inputCls} appearance-none bg-pl-bg`}
                      value={form.primaryNeed}
                      onChange={(e) => setField("primaryNeed", e.target.value)}
                      aria-invalid={Boolean(errors.primaryNeed)}
                    >
                      <option value="">Select…</option>
                      {PRIMARY_NEEDS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
              </div>

              {/* § III — In Your Words */}
              <SectionLabel n="III" label="In Your Words" />

              <div className="entry e-4 mb-16 md:mb-20 space-y-8">
                <p className="text-15 md:text-16 leading-[1.78] text-pl-text-secondary">
                  These two questions are optional, but they are what we read
                  most closely.
                </p>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <Field
                    label="What task recurs most in your week?"
                    htmlFor="apply-recurring"
                  >
                    <textarea
                      id="apply-recurring"
                      rows={4}
                      className={`${inputCls} resize-y`}
                      value={form.recurringTask}
                      onChange={(e) =>
                        setField("recurringTask", e.target.value)
                      }
                    />
                  </Field>

                  <Field
                    label="What is the hardest problem in your practice right now?"
                    htmlFor="apply-hardest"
                  >
                    <textarea
                      id="apply-hardest"
                      rows={4}
                      className={`${inputCls} resize-y`}
                      value={form.hardestProblem}
                      onChange={(e) =>
                        setField("hardestProblem", e.target.value)
                      }
                    />
                  </Field>
                </div>
              </div>

              {/* § IV — Terms of Membership */}
              <SectionLabel n="IV" label="Terms of Membership" />

              <div className="entry e-5">
                <div className="border border-pl-border rounded-pl-lg p-6 md:p-8 mb-8">
                  <ul className="list-disc list-outside pl-5 lg:columns-2 lg:gap-x-14 space-y-4 text-15 md:text-16 leading-[1.7] text-pl-text-secondary [&>li]:break-inside-avoid">
                    <li>
                      Nothing is payable now, and this application does not
                      commit you to anything.
                    </li>
                    <li>
                      If your application is accepted, you receive{" "}
                      <strong className="text-pl-text-primary font-medium">
                        {membership.trialDays} days of full access
                      </strong>{" "}
                      to the legal workspace, free of charge. No payment or
                      card details are required.
                    </li>
                    <li>
                      At the end of the {membership.trialDays} days, you decide
                      whether to become a member. Nothing is charged unless
                      you choose to continue.
                    </li>
                    <li>
                      Membership after the free period is{" "}
                      {membership.monthlyFee} per month, or{" "}
                      {membership.yearlyFee} per year (
                      {membership.yearlyNote}).
                    </li>
                    <li>
                      Seats are limited, so that we can support each
                      member&rsquo;s professional success.
                    </li>
                  </ul>
                </div>

                <label
                  htmlFor="apply-terms-ack"
                  className="flex items-start gap-4 cursor-pointer mb-2"
                >
                  <input
                    id="apply-terms-ack"
                    type="checkbox"
                    className="mt-1 h-[18px] w-[18px] shrink-0 accent-[var(--pl-accent)] cursor-pointer"
                    checked={form.termsAcknowledged}
                    onChange={(e) =>
                      setField("termsAcknowledged", e.target.checked)
                    }
                    aria-invalid={Boolean(errors.termsAcknowledged)}
                  />
                  <span className="text-15 md:text-16 leading-[1.7] text-pl-text-secondary">
                    I have read the terms of membership above and apply on that basis.
                  </span>
                </label>
                {errors.termsAcknowledged && (
                  <p className={errorCls}>{errors.termsAcknowledged}</p>
                )}

                <div className="mt-10">
                  <button
                    type="submit"
                    disabled={status === "submitting" || status === "checking"}
                    className="group inline-flex items-center gap-3 px-7 py-4 bg-pl-text-primary text-pl-bg rounded-pl font-mono text-11 md:text-12 uppercase tracking-[0.2em] hover:bg-pl-accent transition-colors duration-200 disabled:opacity-60 disabled:hover:bg-pl-text-primary"
                  >
                    {status === "checking"
                      ? "Checking…"
                      : status === "submitting"
                        ? "Submitting…"
                        : "Submit application"}
                    <span className="transition-transform duration-200 group-hover:translate-x-1">
                      →
                    </span>
                  </button>

                  {status === "auth-required" && (
                    <p className="mt-5 text-14 leading-[1.7] text-pl-error">
                      Please sign in to your paralegal.lk account before
                      submitting a membership application.
                    </p>
                  )}

                  {status === "error" && (
                    <p className="mt-5 text-14 leading-[1.7] text-pl-error">
                      Something went wrong and your application was not
                      submitted. Please try again, or email it to{" "}
                      <a
                        href="mailto:admin@paralegal.lk?subject=Membership%20application"
                        className="text-pl-accent font-medium hover:underline"
                      >
                        admin@paralegal.lk
                      </a>
                      .
                    </p>
                  )}

                  <p className="mt-6 text-13 leading-[1.7] text-pl-text-tertiary max-w-[56ch]">
                    Your details are used only to assess this application. If
                    your application is not accepted, they are deleted within
                    90 days. See our{" "}
                    <a
                      href="/privacy"
                      className="text-pl-accent hover:underline"
                    >
                      privacy policy
                    </a>
                    .
                  </p>
                </div>
              </div>
            </form>
          </>
        )}

        <style>{`
          .apply-page .entry { opacity: 0; transform: translateY(12px); animation: am-in .75s cubic-bezier(.2,.7,.2,1) forwards; }
          .apply-page .e-0 { animation-delay: .04s; }
          .apply-page .e-1 { animation-delay: .10s; }
          .apply-page .e-2 { animation-delay: .14s; }
          .apply-page .e-3 { animation-delay: .18s; }
          .apply-page .e-4 { animation-delay: .22s; }
          .apply-page .e-5 { animation-delay: .26s; }

          @keyframes am-in { to { opacity: 1; transform: translateY(0); } }

          @media (prefers-reduced-motion: reduce) {
            .apply-page .entry { animation: none; opacity: 1; transform: none; }
          }
        `}</style>
      </article>
    </MainLayout>
  );
};

const APPLICATION_STATUS_COPY = {
  new: {
    eyebrow: "Application received",
    headline: "Thank you. Your application has been received.",
    body: "Every application is read personally. We will reply to {email} within {days} working days.",
  },
  reviewing: {
    eyebrow: "Application in review",
    headline: "Your application is currently in review.",
    body: "Every application is read personally. We will reply to {email} within {days} working days.",
  },
  waitlist: {
    eyebrow: "Application waitlisted",
    headline: "Your application is currently waitlisted.",
    body: "We may contact {email} if a suitable Professional Membership place becomes available.",
  },
  accepted: {
    eyebrow: "Application accepted",
    headline: "Your Professional Membership application has been accepted.",
    body: "Please sign in with the same account and continue to the Professional Plan page.",
  },
  declined: {
    eyebrow: "Application declined",
    headline: "Your Professional Membership application was not accepted at this time.",
    body: "If you believe this is an error or your circumstances have changed, contact admin@paralegal.lk.",
  },
};

const formatApplicantMessage = (template, email) => template
  .replace("{email}", email || "the email address you provided")
  .replace("{days}", String(MEMBERSHIP_DEFAULTS.replyWithinDays));

const SubmittedNotice = ({ email, existingApplication }) => {
  const normalizedStatus = String(existingApplication?.status || "new").toLowerCase();
  const copy = APPLICATION_STATUS_COPY[normalizedStatus] || APPLICATION_STATUS_COPY.new;
  const contactEmail = email || existingApplication?.email || "";
  const showGenericContact = ["new", "reviewing", "waitlist"].includes(normalizedStatus);

  return (
    <div className="entry e-0 max-w-[64ch] py-8 md:py-14">
      <p className="font-mono text-10 md:text-11 uppercase tracking-[0.22em] text-pl-accent mb-6">
        {copy.eyebrow}
      </p>
      <h1 className="font-display leading-[1.1] tracking-[-0.015em] text-[30px] sm:text-[38px] md:text-[48px] mb-8">
        {copy.headline}
      </h1>
      <p className="text-15 md:text-16 leading-[1.78] text-pl-text-secondary mb-5">
        {formatApplicantMessage(copy.body, contactEmail)}
      </p>
      {normalizedStatus === "accepted" ? (
        <p className="text-15 md:text-16 leading-[1.78] text-pl-text-secondary mb-5">
          Continue at{" "}
          <a href="/professional-plan" className="text-pl-accent font-medium hover:underline">
            the Professional Plan page
          </a>
          .
        </p>
      ) : null}
      {showGenericContact ? (
        <p className="text-15 md:text-16 leading-[1.78] text-pl-text-secondary">
          If anything changes in the meantime, write to us at{" "}
          <a
            href="mailto:admin@paralegal.lk"
            className="text-pl-accent font-medium hover:underline"
          >
            admin@paralegal.lk
          </a>
          .
        </p>
      ) : null}
    </div>
  );
};

export default ApplyForMembership;
